import React, { useEffect, useState } from "react";
import { 
  FaQuestionCircle, 
  FaChevronDown, 
  FaHeadset, 
  FaPhoneAlt, 
  FaWhatsapp, 
  FaFlask, 
  FaTruck, 
  FaFilePdf, 
  FaCreditCard 
} from "react-icons/fa";
import { Link } from "react-router-dom";

export default function HelpSupport() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const fallbackFAQs = [
    {
      category: "booking",
      question: "How do I book a lab test or health checkup on MedrphaLab?",
      answer: "You can easily select your desired test or health package from the All Tests / All Packages page, choose your convenient date and time slot, enter your address, and complete the booking in under 2 minutes."
    },
    {
      category: "collection",
      question: "How does free home sample collection work?",
      answer: "A certified, vaccinated phlebotomist visits your doorstep at the chosen time slot equipped with a sterile single-use collection kit and a temperature-controlled cold box to safely transport your sample to our accredited partner lab."
    },
    {
      category: "reports",
      question: "How and when will I receive my diagnostic test report?",
      answer: "Digital test reports verified with digital pathologist signatures are delivered within 6 to 24 hours directly on your registered WhatsApp number and are always accessible in your MedrphaLab Patient Portal."
    },
    {
      category: "reports",
      question: "Are MedrphaLab partner laboratories NABL & ISO certified?",
      answer: "Yes, 100% of our partner diagnostic laboratories are NABL (National Accreditation Board for Testing and Calibration Laboratories) and ISO accredited, ensuring hospital-grade clinical precision."
    },
    {
      category: "payment",
      question: "What payment methods are supported?",
      answer: "We support UPI (Google Pay, PhonePe, Paytm), Credit & Debit Cards, Net Banking, and Cash On Collection (Pay after sample collection at your home)."
    },
    {
      category: "booking",
      question: "Can I reschedule or cancel my sample collection appointment?",
      answer: "Yes, you can reschedule or cancel your appointment at any time before the phlebotomist arrives by calling our 24x7 helpline at +91 81783 99885."
    }
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      if (API_BASE_URL) {
        const response = await fetch(`${API_BASE_URL}Rating/GetAllQuestions`);
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setQuestions(data);
          return;
        }
      }
      setQuestions(fallbackFAQs);
    } catch (error) {
      console.error("API Error:", error);
      setQuestions(fallbackFAQs);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 text-slate-800">
      
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white py-16 md:py-20 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white border border-white/20 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <FaHeadset className="text-amber-300" />
            <span>24x7 Customer Helpdesk</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight">
            How Can We Help You?
          </h1>
          <p className="mt-3 text-xs sm:text-sm text-red-100 max-w-xl mx-auto">
            Find instant answers to common questions regarding sample pickup, reports, and payments.
          </p>
        </div>
      </section>

      {/* QUICK CONTACT CHANNELS */}
      <section className="max-w-5xl mx-auto px-4 -mt-8 relative z-20">
        <div className="grid sm:grid-cols-3 gap-4">
          <a
            href="tel:8178399885"
            className="bg-white p-5 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl hover:border-red-300 transition-all flex items-center gap-3.5 group"
          >
            <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-all">
              <FaPhoneAlt size={16} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Call Medical Desk</span>
              <strong className="text-xs sm:text-sm text-slate-900">+91 81783 99885</strong>
            </div>
          </a>

          <a
            href="https://wa.me/918178399885"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white p-5 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl hover:border-emerald-300 transition-all flex items-center gap-3.5 group"
          >
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-all">
              <FaWhatsapp size={18} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">WhatsApp Support</span>
              <strong className="text-xs sm:text-sm text-slate-900">Chat Instantly</strong>
            </div>
          </a>

          <Link
            to="/all-packages"
            className="bg-white p-5 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl hover:border-red-300 transition-all flex items-center gap-3.5 group"
          >
            <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:bg-rose-600 group-hover:text-white transition-all">
              <FaFlask size={16} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Book Diagnostic Test</span>
              <strong className="text-xs sm:text-sm text-slate-900">View 68+ Tests</strong>
            </div>
          </Link>
        </div>
      </section>

      {/* FAQ ACCORDION LIST */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3.5 py-1 rounded-full border border-red-100">
            Frequently Asked Questions
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mt-2 tracking-tight">
            Popular Answers & Guidance
          </h2>
        </div>

        {/* LOADING SKELETON */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-white border border-slate-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {/* ACCORDION ITEMS */}
        {!loading && (
          <div className="space-y-3.5">
            {questions.map((item, index) => {
              const qText = item.qusetion || item.question || item.questionName;
              const aText = item.answer || item.ans;
              const isOpen = activeAccordion === index;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-xs ${
                    isOpen ? "border-red-400 ring-2 ring-red-50" : "border-slate-200/90 hover:border-slate-300"
                  }`}
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-xs font-black shrink-0">
                        Q
                      </span>
                      {qText}
                    </span>
                    <FaChevronDown
                      size={12}
                      className={`text-slate-400 transition-transform duration-200 shrink-0 ${
                        isOpen ? "rotate-180 text-red-600" : ""
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 mt-1 bg-slate-50/50">
                      {aText}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

    </div>
  );
}
